import { useId } from "react";
import { Link } from "@tanstack/react-router";
import { useAuth } from "../contexts/AuthContext";
import { getSafeImageSrc } from "../utils/util";
import defaultProfileImg from "../assets/profile-img.svg";

const SettingsIcon = () => {
  const clipId = useId();
  return (
  <svg
    id="Group_40"
    data-name="Group 40"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 26.942 26.942"
    aria-hidden
    width="26.942"
    height="26.942"
  >
    <defs>
      <clipPath id={clipId}>
        <rect
          id="Rectangle_48"
          data-name="Rectangle 48"
          width="26.942"
          height="26.942"
          fill="#e761a0"
        />
      </clipPath>
    </defs>
    <g id="Group_39" data-name="Group 39" clipPath={`url(#${clipId})`}>
      <path
        id="Path_186"
        data-name="Path 186"
        d="M22.887,10.319A9.991,9.991,0,0,0,22.4,9.127l2.072-2.072a1.155,1.155,0,0,0,0-1.634L21.584,2.534a1.155,1.155,0,0,0-1.634,0L17.9,4.585a9.767,9.767,0,0,0-1.187-.5V1.155A1.156,1.156,0,0,0,15.557,0H11.474a1.156,1.156,0,0,0-1.155,1.155v2.9a9.923,9.923,0,0,0-1.192.487L7.055,2.47a1.155,1.155,0,0,0-1.634,0L2.533,5.357a1.155,1.155,0,0,0,0,1.634L4.585,9.042a9.841,9.841,0,0,0-.5,1.187H1.155A1.155,1.155,0,0,0,0,11.385v4.083a1.156,1.156,0,0,0,1.155,1.155h2.9a9.886,9.886,0,0,0,.487,1.192L2.47,19.887a1.155,1.155,0,0,0,0,1.634l2.887,2.887a1.155,1.155,0,0,0,1.634,0l2.051-2.051a9.954,9.954,0,0,0,1.187.5v2.93a1.155,1.155,0,0,0,1.155,1.155h4.083a1.155,1.155,0,0,0,1.155-1.155v-2.9a9.937,9.937,0,0,0,1.192-.488l2.072,2.073a1.156,1.156,0,0,0,1.634,0l2.887-2.887a1.156,1.156,0,0,0,0-1.634L22.357,17.9a9.954,9.954,0,0,0,.5-1.187h2.931a1.155,1.155,0,0,0,1.155-1.155V11.474a1.155,1.155,0,0,0-1.155-1.155Zm-9.416,9.759a6.607,6.607,0,1,1,6.606-6.607,6.614,6.614,0,0,1-6.606,6.607"
        fill="#e761a0"
      />
    </g>
  </svg>
  );
};

interface UserProfileCardProps {
  name?: string;
  email?: string;
  manageAccountTo?: string;
  profileImageUrl?: string | null;
  className?: string;
  /** Compact inline variant for header (e.g. next to toggle menu) */
  variant?: "default" | "compact";
}

const UserProfileCard = ({
  name: nameProp,
  email: emailProp,
  manageAccountTo = "/manage-account",
  profileImageUrl,
  className = "",
  variant = "default",
}: UserProfileCardProps) => {
  const { userDetails } = useAuth();
  const email = emailProp ?? userDetails?.email ?? "";
  const derivedName = (nameProp ?? userDetails?.fullName ?? "").trim();
  const name = derivedName;
  const rawProfileImg =
    profileImageUrl ??
    userDetails?.profileImagePreview ??
    userDetails?.profileImage ??
    null;
  const profileImg = getSafeImageSrc(rawProfileImg, defaultProfileImg);

  if (variant === "compact") {
    return (
      <Link
        to={manageAccountTo}
        aria-label="Manage account"
        className={`user-profile-card-compact inline-flex items-center justify-center p-2 text-[#E761A0] hover:opacity-90 transition-opacity shrink-0 ${className}`}
      >
        <span className="[&_svg]:w-5 [&_svg]:h-5">
          <SettingsIcon />
        </span>
      </Link>
    );
  }

  return (
    <div
      className={`profile-card-in-content relative z-10 bg-transparent backdrop-blur-sm rounded-[18px] border border-[#868686] shadow-lg overflow-hidden max-w-[386px] ml-auto mb-[16px] xl:mt-0 mt-[30px] ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-6 lg:p-[15px] p-[10px] ">
        <div className="flex flex-col min-w-0 max-w-[190px]">
          <h2 className="text-[17px] font-weight-500 mb-0 text-white truncate ">
            {name}
          </h2>
          <p className="text-[13px] text-white truncate font-weight-300">
            {email}
          </p>
          <Link
            to={manageAccountTo}
            className="inline-flex items-center mt-2 py-1 pr-1 -mr-1 text-sm font-medium text-white hover:opacity-90 transition-opacity group cursor-pointer"
          >
            <span
              style={{ color: "var(--primary-color)" }}
              className="transition-transform duration-300 group-hover:rotate-90"
            >
              <SettingsIcon />
            </span>

            <span className="text-[13px] font-bold text-[#989898] ml-[12px]">
              MANAGE ACCOUNT
            </span>
          </Link>
        </div>
        <div className="shrink-0">
          <div className="w-[72px] h-[72px] rounded-full bg-gray-600/50 border-2 border-white/10 flex items-center justify-center overflow-hidden">
            <img
              src={profileImg || undefined}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileCard;
